import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  DeterminateCircularProgressIndicator,
  Dialog,
  IOmit,
} from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type {
  IApiCrowdSearchFiltersRequestBody,
  IApiCrowdSearchItemResponseBody,
  IApiCrowdSearchRequestBody,
} from '~/lib/api/modules/Crowd';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { useSearchCrowdQuery } from '~/lib/api/modules/Crowd/hooks/useSearchCrowdQuery';

type ICsvExportCellType = string | undefined | null;

function escapeCsvCell(cell: ICsvExportCellType): string {
  const cellStr = cell || '';
  if (/[,"]/.exec(cellStr)) {
    return `"${cellStr.replace(/"/g, '""')}"`;
  }

  return cellStr;
}

function serializeCsvRow(row: Array<ICsvExportCellType>): string {
  return row.map(escapeCsvCell).join(',') + '\n';
}

function convertAsCsv(
  data: Array<IApiCrowdSearchItemResponseBody>,
  t: (x: string) => string,
): string {
  let output = serializeCsvRow([
    t('ExportCommunityResultsDialog.csvHeadline.deviceId'),
    t('ExportCommunityResultsDialog.csvHeadline.userId'),
    t('ExportCommunityResultsDialog.csvHeadline.username'),
    t('ExportCommunityResultsDialog.csvHeadline.firstname'),
    t('ExportCommunityResultsDialog.csvHeadline.lastname'),
    t('ExportCommunityResultsDialog.csvHeadline.email'),
    t('ExportCommunityResultsDialog.csvHeadline.badges'),
  ]);
  data.forEach((result) => {
    const row = serializeCsvRow([
      result.deviceId,
      result.userId,
      result.user?.profile?.username,
      result.user?.profile?.firstname,
      result.user?.profile?.lastname,
      result.user?.profile?.email ||
        (result.user?.emails ? result.user.emails[0].address : ''),
      result.user?.badges?.map((badge) => badge.id).join(','),
    ]);

    output += row;
  });
  return output;
}

function downloadCsv(csv: string): undefined {
  const blobx = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blobx);
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.download = 'export.csv';
  link.click();
}

const styles = stylex.create({
  progressIndicator: {
    fontSize: '2em',
  },
});

export type IExportCommunityResultsDialogProps = IContainerProps & {
  selectedCount: number;
  opened: boolean;
  onClose: () => void;
  appId: string;
  filters?: IOmit<IApiCrowdSearchFiltersRequestBody, 'type'> & {
    type?: string;
  };
  type: IApiCrowdSearchRequestBody['type'];
};

export const ExportCommunityResultsDialog: React.FC<
  IExportCommunityResultsDialogProps
> = (props) => {
  const { sx, opened, onClose, appId, selectedCount, filters, type } = props;
  const { t } = useTranslation();
  const [downloadEnded, setDownloadEnded] = useState(false);
  const [downloadedCount, setDownloadedCount] = useState(0);
  const [exportError, setExportError] = useState<null | Error>(null);
  const exportedDataRef = useRef<Array<IApiCrowdSearchItemResponseBody>>([]);

  const searchCrowdQuery = useSearchCrowdQuery(
    {
      appId,
      body: {
        ...filters,
        type,
        skip: downloadedCount,
        limit: 100,
      },
    },
    {
      enabled: opened && !downloadEnded,
      cacheTime: 0,
    },
  );

  useEffect(() => {
    if (opened) {
      setDownloadEnded(false);
      setDownloadedCount(0);
      setExportError(null);
      exportedDataRef.current = [];
    } else {
      setDownloadEnded(true);
    }
  }, [opened]);

  useEffect(() => {
    if (
      opened &&
      !downloadEnded &&
      !searchCrowdQuery.isLoading &&
      downloadedCount < selectedCount
    ) {
      if (searchCrowdQuery.error) {
        setExportError(searchCrowdQuery.error);
      } else if (searchCrowdQuery.data?.items) {
        exportedDataRef.current = exportedDataRef.current.concat(
          searchCrowdQuery.data.items,
        );
        setDownloadedCount(exportedDataRef.current.length);
      } else {
        setExportError(
          new Error(t('ExportCommunityResultsDialog.errors.noReturnedData')),
        );
      }

      if (exportedDataRef.current.length >= selectedCount) {
        setDownloadEnded(true);
      }
    }
  }, [
    downloadEnded,
    downloadedCount,
    opened,
    searchCrowdQuery.data,
    searchCrowdQuery.error,
    searchCrowdQuery.isLoading,
    selectedCount,
    t,
  ]);

  const handleDownload = useCallback(() => {
    const csv = convertAsCsv(exportedDataRef.current, t);
    downloadCsv(csv);
    onClose();
  }, [onClose, t]);

  return (
    <Dialog
      sx={sx}
      opened={opened}
      onClose={onClose}
      headline={t('ExportCommunityResultsDialog.title')}
      actions={({ close }) => (
        <>
          {downloadEnded && (
            <Button variant="text" onClick={handleDownload}>
              {t('ExportCommunityResultsDialog.buttons.download')}
            </Button>
          )}
          <Button variant="text" onClick={close}>
            {t('common.cancel')}
          </Button>
        </>
      )}
    >
      {exportError ? (
        <ErrorMessage error={exportError} />
      ) : (
        <p
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {`${downloadedCount} / ${selectedCount}`}
          <DeterminateCircularProgressIndicator
            sx={styles.progressIndicator}
            value={downloadedCount / selectedCount}
            withLabel
          />
        </p>
      )}
    </Dialog>
  );
};
